// ═══════════════════════════════════════════════════════════════════════════
// OnboardingFlow — full-screen 4-screen first-run intake.
//
// Visually mirrors the public landing page at meetepio.com: dark
// near-black base (#080A0E) + SVG film-grain overlay + three fixed
// radial atmospheric blobs (teal bottom-left, orange-red bottom-right,
// warm center glow). The grain, blobs, and logo are FIXED so they
// persist across all screen transitions; only the center content fades.
//
// Props:
//   onComplete(data)  — called when the user finishes the flow with
//                       { show_status, reactivation_user, show_name,
//                       niches }. Triggered on screen 4 mount BEFORE
//                       the CTA click; the CTA itself just navigates
//                       (no re-trigger).
//   entryPoint        — 'fresh' | 'chat'
//                       'chat' skips screen 2 (show_status) because
//                       the user already demonstrated intent by using
//                       the landing chat.
//
// Self-contained: manages its own screen state internally. The
// surrounding app shell only needs to mount/unmount it and receive
// onComplete.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';

// ── Color tokens ────────────────────────────────────────────────────────
const BG            = '#080A0E';
const SURFACE       = 'rgba(255,255,255,0.04)';
const SURFACE_HOVER = 'rgba(255,255,255,0.07)';
const BORDER        = 'rgba(255,255,255,0.08)';
const BORDER_HOVER  = 'rgba(255,255,255,0.15)';
const BORDER_ACTIVE = 'rgba(232,98,42,0.6)';
const TEXT          = '#FFFFFF';
const TEXT_MUTED    = 'rgba(255,255,255,0.5)';
const TEXT_HINT     = 'rgba(255,255,255,0.3)';
const ORANGE        = '#c6364a';
const ORANGE_HOVER  = '#F07840';
const GOLD          = '#D4A017';
const LABEL_BLUE    = '#6B9FD4';

const NICHES = [
  'Relationships',
  'Emotional Intelligence',
  'Self Improvement',
  'Masculinity',
  'Mental Health',
  'Business',
  'True Crime',
  'Comedy',
  'Health & Fitness',
  'Spirituality',
  'Finance',
  'Parenting',
  'Technology',
];

const SHOW_STATUS_OPTIONS = [
  { id: 'posting_no_views',  label: "Posting but not getting the views I deserve",       reactivation: false },
  { id: 'stopped',           label: "I stopped — couldn't figure out why it wasn't growing", reactivation: true  },
  { id: 'just_starting',     label: "Just getting started and want to do it right",       reactivation: false },
  { id: 'growing_leaving',   label: "Growing, but I know I'm leaving views on the table", reactivation: false },
];

const MAX_NICHES = 3;

export default function OnboardingFlow({ onComplete, entryPoint = 'fresh' }) {
  // Screen index — 0..3. The chat entry point skips screen 1 (where
  // are you with your podcast) entirely; we re-map advancement so
  // 0 → 2 → 3 instead of 0 → 1 → 2 → 3.
  const [screen, setScreen] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  // Local form state
  const [showStatus, setShowStatus] = useState(null);   // option id
  const [showName,   setShowName]   = useState('');
  const [niches,     setNiches]     = useState([]);

  const isChatEntry = entryPoint === 'chat';

  const reactivationUser = useMemo(() => {
    const opt = SHOW_STATUS_OPTIONS.find((o) => o.id === showStatus);
    return !!opt?.reactivation;
  }, [showStatus]);

  // ── Screen advancement with cross-fade ─────────────────────────────────
  // Two phase fade: outgoing content opacity 1→0 + translateY 0→-10
  // (150ms ease-in), then we swap and the incoming content opacity 0→1
  // + translateY 10→0 (200ms ease-out). The fixed background layers
  // (grain, blobs, logo) DO NOT animate — only the content container.
  function advance(toScreen) {
    if (transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setScreen(toScreen);
      setTransitioning(false);
    }, 150);
  }

  function handleScreen0Continue() {
    // Welcome → status (or skip to show on chat entry)
    advance(isChatEntry ? 2 : 1);
  }
  function handleScreen1Continue() {
    advance(2);
  }
  function handleScreen2Continue() {
    advance(3);
  }

  function toggleNiche(n) {
    setNiches((cur) => {
      if (cur.includes(n)) return cur.filter((x) => x !== n);
      if (cur.length >= MAX_NICHES) return cur;
      return [...cur, n];
    });
  }

  // ── Screen 4 fires onComplete on mount ─────────────────────────────────
  // Per spec, completion is determined by REACHING screen 4 — not by
  // clicking the CTA. The CTA itself just navigates (the parent decides
  // where to). We use a one-shot ref to make sure onComplete only fires
  // once even if screen 4 re-renders.
  const completionFiredRef = React.useRef(false);
  useEffect(() => {
    if (screen !== 3) return;
    if (completionFiredRef.current) return;
    completionFiredRef.current = true;
    onComplete?.({
      show_status:        showStatus || null,
      reactivation_user:  reactivationUser,
      show_name:          showName.trim() || null,
      niches:             niches.slice(),
    });
  }, [screen, showStatus, reactivationUser, showName, niches, onComplete]);

  // The "Create my first episode" CTA — does nothing besides being
  // visually present. Per spec the parent's onComplete handler has
  // already fired on mount and is responsible for navigating once it
  // resolves. Leaving the click as a no-op (with a press animation
  // for tactile feedback) so impatient users don't get stuck.
  function handleScreen3Cta() { /* parent handles navigation */ }

  // ── Render ─────────────────────────────────────────────────────────────
  const contentStyle = {
    ...S.container,
    opacity:   transitioning ? 0 : 1,
    transform: transitioning ? 'translateY(-10px)' : 'translateY(0)',
    transition: transitioning
      ? 'opacity 150ms ease-in, transform 150ms ease-in'
      : 'opacity 200ms ease-out, transform 200ms ease-out',
  };

  return (
    <div style={S.shell}>
      {/* Layer 1 — base color is on .shell. Layer 2 — film grain. */}
      <GrainFilter />
      <div style={S.grain} aria-hidden="true" />

      {/* Layer 3 — atmospheric blobs (fixed, persist across screens). */}
      <div style={S.blobTeal}    aria-hidden="true" />
      <div style={S.blobOrange}  aria-hidden="true" />
      <div style={S.blobCenter}  aria-hidden="true" />

      {/* Logo — fixed top-left, persists across screens. */}
      <div style={S.logo}>epio<span style={{ color: '#c6364a' }}>.</span></div>

      {/* Center content — the only thing that fades on advance. */}
      <div style={contentStyle}>
        {screen === 0 && <ScreenWelcome onContinue={handleScreen0Continue} />}
        {screen === 1 && (
          <ScreenStatus
            value={showStatus}
            onChange={setShowStatus}
            onContinue={handleScreen1Continue}
            entryPoint={entryPoint}
          />
        )}
        {screen === 2 && (
          <ScreenShow
            showName={showName}
            onShowName={setShowName}
            niches={niches}
            onToggleNiche={toggleNiche}
            onContinue={handleScreen2Continue}
            entryPoint={entryPoint}
          />
        )}
        {screen === 3 && (
          <ScreenPayoff
            niches={niches}
            onCta={handleScreen3Cta}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Screens ──────────────────────────────────────────────────────────── */

function ScreenWelcome({ onContinue }) {
  return (
    <>
      <Label color={LABEL_BLUE}>FOR PODCASTERS WHO ARE DONE GUESSING</Label>
      <Headline>
        You deserve an audience<br />
        that actually <em style={S.italic}>shows up.</em>
      </Headline>
      <Subtext>
        You've been doing the work.<br />
        It's time the right people found you.
      </Subtext>
      <PrimaryButton onClick={onContinue}>Let's go</PrimaryButton>
      <div style={S.signinRow}>
        Have an account?{' '}
        <button
          type="button"
          style={S.signinLink}
          onClick={() => {
            try {
              window.dispatchEvent(new CustomEvent('onboarding:signin'));
            } catch { /* SSR / no-window */ }
          }}
        >
          Sign in
        </button>
      </div>
    </>
  );
}

function ScreenStatus({ value, onChange, onContinue, entryPoint }) {
  // entryPoint is fresh here by definition (chat entry skips this screen).
  // Step copy reflects the fresh-entry numbering.
  return (
    <>
      <Label>STEP 1 OF 2</Label>
      <Headline>
        Where are you with{' '}
        <em style={S.italic}>your podcast</em><br />
        right now?
      </Headline>
      <Subtext>
        No wrong answer — this shapes<br />
        everything we show you.
      </Subtext>
      <div style={{ marginTop: 28, marginBottom: 8 }}>
        {SHOW_STATUS_OPTIONS.map((o) => (
          <OptionCard
            key={o.id}
            selected={value === o.id}
            onClick={() => onChange(o.id)}
            label={o.label}
          />
        ))}
      </div>
      <PrimaryButton onClick={onContinue} disabled={!value}>Continue</PrimaryButton>
      <ProgressDots count={2} active={0} />
    </>
  );
}

function ScreenShow({ showName, onShowName, niches, onToggleNiche, onContinue, entryPoint }) {
  const isChatEntry  = entryPoint === 'chat';
  const stepLabel    = isChatEntry ? 'STEP 1 OF 1' : 'STEP 2 OF 2';
  const dotsCount    = isChatEntry ? 1 : 2;
  const dotsActive   = isChatEntry ? 0 : 1;
  const canContinue  = showName.trim().length > 0 && niches.length > 0;
  const atMaxNiches  = niches.length >= MAX_NICHES;

  return (
    <>
      <Label>{stepLabel}</Label>
      <Headline>
        What's your show <em style={S.italic}>about?</em>
      </Headline>
      <Subtext>
        Just the basics. We'll do the rest.
      </Subtext>

      <div style={S.fieldGroup}>
        <div style={S.fieldLabel}>SHOW NAME</div>
        <FocusableInput
          type="text"
          value={showName}
          onChange={(e) => onShowName(e.target.value.slice(0, 80))}
          placeholder="e.g. The Daily Stoic"
          maxLength={80}
        />
      </div>

      <div style={S.fieldGroup}>
        <div style={{ ...S.fieldLabel, display: 'flex', justifyContent: 'space-between' }}>
          <span>YOUR NICHE</span>
          <span style={{ color: TEXT_HINT }}>{niches.length}/{MAX_NICHES}</span>
        </div>
        <div style={S.nicheRow}>
          {NICHES.map((n) => {
            const selected = niches.includes(n);
            const dimmed   = !selected && atMaxNiches;
            return (
              <button
                key={n}
                type="button"
                style={{
                  ...S.nichePill,
                  ...(selected ? S.nichePillOn : {}),
                  ...(dimmed ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                onClick={() => { if (!dimmed) onToggleNiche(n); }}
                aria-pressed={selected}
                disabled={dimmed}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>

      <PrimaryButton onClick={onContinue} disabled={!canContinue}>
        Take me in
      </PrimaryButton>
      <ProgressDots count={dotsCount} active={dotsActive} />
    </>
  );
}

function ScreenPayoff({ niches, onCta }) {
  const [n1, n2] = niches;
  const niceList = n2 ? `${n1} & ${n2}` : (n1 || 'your niche');
  return (
    <>
      <Waveform />
      <Headline style={{ marginTop: 18 }}>
        Your next episode is going to{' '}
        <em style={S.italic}>hit different.</em>
      </Headline>
      <p style={S.subtext}>
        We've tuned everything for{' '}
        <span style={{ color: TEXT }}>{niceList}.</span>
      </p>
      <p style={{ ...S.subtext, marginTop: 0 }}>
        The audience you've been building toward<br />
        is closer than you think.
      </p>

      <div style={S.scoreCard}>
        <div style={S.scoreLabel}>SHOW MOMENTUM SCORE</div>
        <div style={S.scoreValue}>—</div>
        <div style={S.scoreSub}>Create your first episode to unlock</div>
      </div>

      <PrimaryButton onClick={onCta}>Create my first episode</PrimaryButton>
    </>
  );
}

/* ─── Reusable bits ────────────────────────────────────────────────────── */

function Label({ children, color }) {
  return (
    <div style={{ ...S.eyebrow, color: color || TEXT_HINT }}>
      {children}
    </div>
  );
}

function Headline({ children, style }) {
  return <h1 style={{ ...S.headline, ...style }}>{children}</h1>;
}

function Subtext({ children }) {
  return <p style={S.subtext}>{children}</p>;
}

function PrimaryButton({ children, onClick, disabled }) {
  const [hover, setHover]   = useState(false);
  const [active, setActive] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      onTouchStart={() => setActive(true)}
      onTouchEnd={() => setActive(false)}
      disabled={disabled}
      style={{
        ...S.primaryBtn,
        ...(hover && !disabled ? { background: ORANGE_HOVER } : {}),
        ...(active && !disabled ? { transform: 'scale(0.98)' } : {}),
        ...(disabled ? S.primaryBtnDisabled : {}),
      }}
    >
      {children}
    </button>
  );
}

function OptionCard({ selected, onClick, label }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...S.optionCard,
        ...(hover && !selected ? S.optionCardHover : {}),
        ...(selected ? S.optionCardSelected : {}),
      }}
      aria-pressed={selected}
    >
      <span style={selected ? { ...S.radio, ...S.radioOn } : S.radio}>
        {selected && <span style={S.radioDot} />}
      </span>
      <span style={selected ? { ...S.optionLabel, color: TEXT } : S.optionLabel}>
        {label}
      </span>
    </button>
  );
}

function FocusableInput({ value, onChange, placeholder, maxLength, type = 'text' }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...S.input,
        ...(focused
          ? { borderColor: BORDER_ACTIVE, background: SURFACE_HOVER }
          : {}),
      }}
    />
  );
}

function ProgressDots({ count, active }) {
  return (
    <div style={S.dots}>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} style={i === active ? { ...S.dot, ...S.dotActive } : S.dot} />
      ))}
    </div>
  );
}

function Waveform() {
  // 5 vertical orange bars, heights 12/20/28/20/12, each animation-delay
  // staggered by 80ms so the wave reads as one rolling pulse. Animation
  // keyframes are injected once in the global head below.
  const heights = [12, 20, 28, 20, 12];
  return (
    <div style={S.wave} aria-hidden="true">
      {heights.map((h, i) => (
        <span
          key={i}
          style={{
            ...S.waveBar,
            height: h,
            animationDelay: `${i * 80}ms`,
          }}
        />
      ))}
    </div>
  );
}

function GrainFilter() {
  return (
    <svg style={{ display: 'none' }} aria-hidden="true">
      <defs>
        <filter id="onboarding-grain">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.65"
            numOctaves="3"
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </defs>
    </svg>
  );
}

/* ─── Inline keyframes (one-time injection) ────────────────────────────── */
// Idempotent — checks for the marker before appending.
if (typeof document !== 'undefined' && !document.getElementById('onboarding-flow-keyframes')) {
  const style = document.createElement('style');
  style.id = 'onboarding-flow-keyframes';
  style.textContent = `
    @keyframes onboardingBarBounce {
      0%, 100% { transform: scaleY(0.5); }
      50%      { transform: scaleY(1); }
    }
    @media (max-width: 480px) {
      .ofl-mobile-blob-teal   { width: 300px !important; height: 300px !important; }
      .ofl-mobile-blob-orange { width: 250px !important; height: 250px !important; }
      .ofl-mobile-blob-center { width: 400px !important; height: 200px !important; }
      .ofl-mobile-headline    { font-size: clamp(22px, 6vw, 30px) !important; }
      .ofl-mobile-container   { padding: 0 20px !important; }
    }
  `;
  document.head.appendChild(style);
}

/* ─── Styles ───────────────────────────────────────────────────────────── */

const S = {
  shell: {
    // position: fixed + inset: 0 takes the flow out of the document so
    // it OVERLAYS AppShell + AuthModal instead of stacking below them.
    // The previous `position: relative` left the component in normal
    // flow, which made the entire onboarding render at the bottom of
    // the page below whatever AppShell rendered. zIndex by itself
    // can't fix that — it only orders elements within the same
    // stacking context, and a relative element shares the page's
    // stacking context with the rest of the layout.
    position: 'fixed',
    inset: 0,
    width: '100vw',
    height: '100vh',
    background: BG,
    color: TEXT,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    zIndex: 1000,
  },

  grain: {
    position: 'fixed',
    inset: 0,
    zIndex: 1,
    filter: 'url(#onboarding-grain)',
    opacity: 0.045,
    pointerEvents: 'none',
  },

  blobTeal: {
    position: 'fixed',
    bottom: '-20%',
    left: '-10%',
    width: 600,
    height: 600,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(20,90,100,0.35) 0%, transparent 70%)',
    filter: 'blur(80px)',
    pointerEvents: 'none',
    zIndex: 2,
  },
  blobOrange: {
    position: 'fixed',
    bottom: '-15%',
    right: '10%',
    width: 500,
    height: 500,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(140,40,15,0.4) 0%, transparent 70%)',
    filter: 'blur(90px)',
    pointerEvents: 'none',
    zIndex: 2,
  },
  blobCenter: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    height: 400,
    background: 'radial-gradient(ellipse, rgba(100,30,10,0.2) 0%, transparent 65%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 2,
  },

  logo: {
    position: 'fixed',
    top: 0, left: 0,
    padding: '24px 32px',
    fontSize: 18,
    fontWeight: 600,
    color: TEXT,
    letterSpacing: '-0.3px',
    zIndex: 20,
  },

  container: {
    position: 'relative',
    zIndex: 10,
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    padding: '0 24px',
    textAlign: 'center',
  },

  eyebrow: {
    fontSize: 11,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: TEXT_HINT,
    fontWeight: 500,
    marginBottom: 18,
  },
  headline: {
    fontSize: 'clamp(28px, 5vw, 40px)',
    fontWeight: 300,
    color: TEXT,
    lineHeight: 1.15,
    letterSpacing: '-0.5px',
    margin: '0 0 14px',
    textAlign: 'center',
  },
  italic: {
    fontStyle: 'italic',
    color: ORANGE,
    fontWeight: 400,
  },
  subtext: {
    fontSize: 15,
    color: TEXT_MUTED,
    lineHeight: 1.65,
    fontWeight: 400,
    maxWidth: 400,
    margin: '0 auto 22px',
    textAlign: 'center',
  },

  primaryBtn: {
    background: ORANGE,
    color: TEXT,
    border: 'none',
    borderRadius: 10,
    padding: '14px 24px',
    fontSize: 15,
    fontWeight: 500,
    width: '100%',
    cursor: 'pointer',
    transition: 'background 0.15s, transform 0.1s',
    fontFamily: 'inherit',
    marginTop: 6,
  },
  primaryBtnDisabled: {
    background: 'rgba(232,98,42,0.25)',
    color: 'rgba(255,255,255,0.3)',
    cursor: 'not-allowed',
  },

  signinRow: {
    marginTop: 18,
    fontSize: 13,
    color: TEXT_MUTED,
  },
  signinLink: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 13,
    color: TEXT_MUTED,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },

  optionCard: {
    background: SURFACE,
    border: `0.5px solid ${BORDER}`,
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    transition: 'all 0.15s',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  optionCardHover: {
    background: SURFACE_HOVER,
    borderColor: BORDER_HOVER,
  },
  optionCardSelected: {
    background: 'rgba(232,98,42,0.08)',
    border: `0.5px solid ${BORDER_ACTIVE}`,
  },
  radio: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '1.5px solid rgba(255,255,255,0.25)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  radioOn: {
    borderColor: ORANGE,
    background: ORANGE,
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: TEXT,
  },
  optionLabel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.75)',
    lineHeight: 1.4,
  },

  fieldGroup: {
    margin: '20px 0',
    textAlign: 'left',
  },
  fieldLabel: {
    fontSize: 11,
    letterSpacing: '0.12em',
    color: TEXT_HINT,
    textTransform: 'uppercase',
    marginBottom: 8,
    fontWeight: 500,
  },
  input: {
    background: 'rgba(255,255,255,0.05)',
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: '13px 16px',
    fontSize: 14,
    color: TEXT,
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s, background 0.15s',
  },
  nicheRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  nichePill: {
    padding: '7px 16px',
    borderRadius: 99,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    color: 'rgba(255,255,255,0.55)',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  nichePillOn: {
    borderColor: BORDER_ACTIVE,
    background: 'rgba(232,98,42,0.1)',
    color: ORANGE,
  },

  dots: {
    display: 'flex',
    gap: 6,
    justifyContent: 'center',
    marginTop: 28,
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.2)',
    transition: 'all 0.2s',
  },
  dotActive: {
    width: 20,
    height: 5,
    borderRadius: 3,
    background: ORANGE,
  },

  scoreCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '0.5px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '16px 20px',
    margin: '20px 0',
    textAlign: 'left',
  },
  scoreLabel: {
    fontSize: 10,
    letterSpacing: '0.12em',
    color: TEXT_HINT,
    textTransform: 'uppercase',
  },
  scoreValue: {
    fontSize: 32,
    fontWeight: 700,
    color: GOLD,
    margin: '4px 0',
    lineHeight: 1,
  },
  scoreSub: {
    fontSize: 12,
    color: TEXT_HINT,
  },

  wave: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 4,
    height: 32,
    marginBottom: 4,
  },
  waveBar: {
    display: 'inline-block',
    width: 4,
    borderRadius: 2,
    background: ORANGE,
    transformOrigin: 'bottom',
    animation: 'onboardingBarBounce 900ms ease-in-out infinite',
  },
};
